import {
  applyEdgeChanges,
  applyNodeChanges,
  Background,
  Controls,
  EdgeChange,
  Edge,
  MiniMap,
  NodeChange,
  Node,
  ReactFlowInstance,
  Panel,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
} from '@xyflow/react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  getWorkflowByCampaignRequest,
  selectSelectedWorkflow,
  updateWorkflowRequest,
} from '../../../store/workflows';
import { v4 as uuidv4 } from 'uuid';
import CustomEdge from '../../../components/workflow/CustomEdge';
import WorkflowNode from '../../../components/workflow/WorkflowNode';
import RightSidePanel from '../../../components/workflow/Panels/rightSidePanel/rightSidePanel';
import CenterPanel from '../../../components/workflow/Panels/centerPanel/centerPanel';
import {
  DelayPanel,
  EmailPanel,
  TriggerPanel,
  TrueFalsePanel,
  WebhookPanel,
} from '../../../components/workflow/nodes';
import { getAttributesRequest } from '../../../store/attributes';
import { getSegmentsRequest } from '../../../store/segments';
import {
  reviewCampaignRequest,
  selectReviewCampaignLoading,
  selectReviews,
} from '../../../store/campaigns';
import ReviewErrorsPanel from '../../../components/workflow/Panels/reviewErrorsPanel/reviewErrorsPanel';
import Loader from '../../../components/ui/Loader';
import { estimateNodeHeight } from '../../../utils/workflowNodeHelpers';
import {
  getLayoutedElements,
  normalizeWorkflowGraph,
  type WorkflowLayoutHint,
} from '../../../utils/workflowLayout';
import {
  WORKFLOW_DND_MIME,
  type WorkflowPaletteDropPayload,
} from '../../../constants/workflowPaletteDnD';
import { findNearestWorkflowEdge } from '../../../utils/workflowEdgeHitTest';

const nodeTypes = {
  action: WorkflowNode,
  trigger: WorkflowNode,
};

const edgeTypes = {
  custom: CustomEdge,
};

const createDefaultNodes = () => {
  const triggerNode: Node = {
    id: uuidv4(),
    type: 'trigger',
    position: { x: 0, y: 0 },
    data: {
      label: 'Trigger',
      status: 'configured',
      type: 'trigger',
      isFixed: true,
      isDefault: true,
    },
    draggable: false,
    deletable: false,
  };
  return { triggerNode };
};

interface WorkflowStepProps {}

type ApplyGraphStateOptions = {
  persist?: boolean;
  layoutHint?: WorkflowLayoutHint;
};

type MergeNodeData = {
  parentConditionId?: string;
  type?: string;
};

type SkeletonFootprint = {
  width: number;
  height: number;
};

const WorkflowStepContent: React.FC<WorkflowStepProps> = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch<any>();

  const workflow = useSelector(selectSelectedWorkflow);
  const reviews = useSelector(selectReviews);
  const reviewLoading = useSelector(selectReviewCampaignLoading);

  const { triggerNode } = useMemo(() => createDefaultNodes(), []);

  const [nodes, setNodes, onNodesChange] = useNodesState([triggerNode]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([] as Edge[]);
  const [isLoadingWorkflow, setIsLoadingWorkflow] = useState(true);
  const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance<
    Node,
    Edge
  > | null>(null);
  const [isPaletteDragging, setIsPaletteDragging] = useState(false);
  const [dropCandidateEdgeId, setDropCandidateEdgeId] = useState<string | null>(
    null
  );
  const [appendMode, setAppendMode] = useState(false);
  const [isNodeDragging, setIsNodeDragging] = useState(false);
  const [draggingGroupNodeIds, setDraggingGroupNodeIds] =
    useState<Set<string> | null>(null);
  const [nodeDragCandidateEdgeId, setNodeDragCandidateEdgeId] = useState<
    string | null
  >(null);
  const [dragSkeletonSize, setDragSkeletonSize] = useState<SkeletonFootprint>({
    width: 310,
    height: 130,
  });
  const dropCandidateEdgeIdRef = useRef<string | null>(null);
  const nodeDragCandidateEdgeIdRef = useRef<string | null>(null);
  const dragGroupDragRef = useRef<{
    primaryId: string;
    startPositions: Record<string, { x: number; y: number }>;
  } | null>(null);
  /** Nodes whose incident edges are ignored during node-drag hit-testing (computed once per drag). */
  const nodeDragExclusionRef = useRef<Set<string>>(new Set());

  const updateDropCandidateEdgeId = useCallback((nextId: string | null) => {
    dropCandidateEdgeIdRef.current = nextId;
    setDropCandidateEdgeId((prev) => (prev === nextId ? prev : nextId));
  }, []);

  const updateNodeDragCandidateEdgeId = useCallback((nextId: string | null) => {
    nodeDragCandidateEdgeIdRef.current = nextId;
    setNodeDragCandidateEdgeId((prev) => (prev === nextId ? prev : nextId));
  }, []);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let frameId: number | null = null;

    if (workflow && workflow.draftFlow) {
      if (
        workflow.draftFlow.nodes &&
        workflow.draftFlow.nodes.length > 0 &&
        workflow.draftFlow.edges
      ) {
        setIsLoadingWorkflow(true);
        frameId = requestAnimationFrame(() => {
          const { nodes: normalizedNodes, edges: normalizedEdges } =
            normalizeWorkflowGraph(
              workflow.draftFlow.nodes,
              workflow.draftFlow.edges
            );
          const { nodes: layoutedNodes, edges: layoutedEdges } =
            getLayoutedElements(normalizedNodes, normalizedEdges, 'TB', {
              operation: 'initialize',
            });
          setNodes(layoutedNodes);
          setEdges(layoutedEdges);
          setIsLoadingWorkflow(false);
        });
      } else {
        timeoutId = setTimeout(() => {
          setIsLoadingWorkflow(false);
        }, 0);
      }
    } else {
      timeoutId = setTimeout(() => {
        setIsLoadingWorkflow(false);
      }, 0);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (frameId !== null) cancelAnimationFrame(frameId);
    };
  }, [workflow, setNodes, setEdges]);

  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [selectedEdge, setSelectedEdge] = useState<Edge | null>(null);
  const [addAfterNode, setAddAfterNode] = useState<{
    nodeId: string;
    branch?: 'true' | 'false' | null;
  } | null>(null);
  const [reviewPanelMode, setReviewPanelMode] = useState<
    'errors' | 'warnings' | null
  >(null);

  const graphBranchMeta = useMemo(() => {
    const sourceNodeIds = new Set<string>();
    const trueBranchSources = new Set<string>();
    const falseBranchSources = new Set<string>();
    const nodeTypeById = new Map<string, string | undefined>();

    nodes.forEach((node) => {
      nodeTypeById.set(node.id, (node.data as any)?.type);
    });

    edges.forEach((edge) => {
      sourceNodeIds.add(edge.source);
      if (edge.sourceHandle === 'true') {
        trueBranchSources.add(edge.source);
      } else if (edge.sourceHandle === 'false') {
        falseBranchSources.add(edge.source);
      }
    });

    const mergeIncomingNodeIds = new Set<string>();
    const incomingByTarget = new Map<string, Edge[]>();

    edges.forEach((edge) => {
      const incoming = incomingByTarget.get(edge.target) || [];
      incoming.push(edge);
      incomingByTarget.set(edge.target, incoming);
    });

    incomingByTarget.forEach((incomingEdges, targetId) => {
      if (nodeTypeById.get(targetId) === 'exit') return;

      const bySource = new Map<string, Edge[]>();
      incomingEdges.forEach((edge) => {
        const sourceList = bySource.get(edge.source) || [];
        sourceList.push(edge);
        bySource.set(edge.source, sourceList);
      });

      bySource.forEach((list, sourceId) => {
        if (nodeTypeById.get(sourceId) !== 'true-false') return;
        const handles = new Set(
          list
            .map((edge: Edge) => edge.sourceHandle)
            .filter(Boolean) as string[]
        );
        if (handles.has('true') && handles.has('false')) {
          mergeIncomingNodeIds.add(targetId);
          return;
        }
      });
    });

    return {
      sourceNodeIds,
      trueBranchSources,
      falseBranchSources,
      mergeIncomingNodeIds,
    };
  }, [nodes, edges]);

  const clearReactFlowSelection = useCallback(() => {
    setNodes((nds: Node[]) =>
      nds.map((node: Node) => ({
        ...node,
        selected: false,
      }))
    );
  }, [setNodes]);

  const clearSelectionState = useCallback(() => {
    setSelectedNode(null);
    setSelectedEdge(null);
    setAddAfterNode(null);
  }, []);

  const [nodeConditions, setNodeConditions] = useState<{
    [key: string]: any[];
  }>({});
  const [nodeConditionsModified, setNodeConditionsModified] = useState(false);

  const triggerConditionsSnapshot = useMemo(() => {
    const triggerNode = nodes.find((node) => node.data?.type === 'trigger');
    if (!triggerNode) {
      return 'no-trigger';
    }

    const config = triggerNode.data?.config as any;
    const conditions = config?.conditions || {};
    const event = config?.event || '';
    const andCount = Array.isArray(conditions.and) ? conditions.and.length : 0;
    const orCount = Array.isArray(conditions.or) ? conditions.or.length : 0;

    return JSON.stringify({
      triggerId: triggerNode.id,
      event,
      andCount,
      orCount,
    });
  }, [nodes]);

  const hasTriggerConditions = useMemo(() => {
    if (triggerConditionsSnapshot === 'no-trigger') {
      return false;
    }

    const triggerMeta = JSON.parse(triggerConditionsSnapshot) as {
      triggerId: string;
      event: string;
      andCount: number;
      orCount: number;
    };

    if (triggerMeta.event) {
      return true;
    }

    return triggerMeta.andCount > 0 || triggerMeta.orCount > 0;
  }, [triggerConditionsSnapshot]);

  const isAttributeSegmentTrigger = useMemo(() => {
    const triggerNode = nodes.find((node) => node.data?.type === 'trigger');
    const triggerType = (triggerNode?.data?.config as any)?.trigger;
    return triggerType !== 'event';
  }, [nodes]);

  useEffect(() => {
    if (isNodeDragging) return;

    const nodesWithMeasurements = nodes.filter((n) => n.measured?.height);
    if (nodesWithMeasurements.length === 0) return;

    const needsRecalculation = nodesWithMeasurements.some((node) => {
      if (!node.measured?.height) return false;
      const estimatedHeight = estimateNodeHeight({
        ...node,
        measured: undefined,
      });
      const actualHeight = node.measured.height;
      return Math.abs(actualHeight - estimatedHeight) > 20;
    });

    if (needsRecalculation) {
      const timeoutId = setTimeout(() => {
        const changedNodeIds = nodesWithMeasurements.map((node) => node.id);
        const { nodes: layoutedNodes, edges: layoutedEdges } =
          getLayoutedElements(nodes, edges, 'TB', {
            operation: 'node-update',
            changedNodeIds,
          });
        setNodes([...layoutedNodes]);
        setEdges([...layoutedEdges]);
      }, 150);

      return () => clearTimeout(timeoutId);
    }
  }, [nodes, edges, isNodeDragging, setNodes, setEdges]);

  const createEdge = useCallback(
    (
      sourceId: string,
      targetId: string,
      options?: { sourceHandle?: string; branch?: 'true' | 'false' }
    ) => {
      if (sourceId === targetId) return null;

      const existingEdge = edges.find(
        (edge) =>
          edge.source === sourceId &&
          edge.target === targetId &&
          edge.sourceHandle === options?.sourceHandle
      );

      if (existingEdge) return null;

      return {
        id: uuidv4(),
        source: sourceId,
        target: targetId,
        type: 'custom',
        sourceHandle: options?.sourceHandle,
        data: options?.branch ? { branch: options.branch } : undefined,
      };
    },
    [edges]
  );

  const getConditionMergeNodeId = useCallback(
    (conditionId: string, nodeList: Node[]) => {
      const mergeNode = nodeList.find(
        (node) =>
          (node.data as MergeNodeData | undefined)?.type === 'merge' &&
          (node.data as MergeNodeData | undefined)?.parentConditionId ===
            conditionId
      );
      return mergeNode?.id;
    },
    []
  );

  const ensureConditionMergeNode = useCallback(
    (conditionId: string, nodeList: Node[]) => {
      const existingMergeId = getConditionMergeNodeId(conditionId, nodeList);
      if (existingMergeId) {
        return { nodes: nodeList, mergeNodeId: existingMergeId };
      }

      const mergeNodeId = uuidv4();
      const mergeNode: Node = {
        id: mergeNodeId,
        type: 'action',
        position: { x: 0, y: 0 },
        data: {
          label: 'Merge',
          status: 'configured',
          type: 'merge',
          parentConditionId: conditionId,
          isFixed: true,
          isDefault: true,
        },
        draggable: false,
        deletable: false,
        selectable: false,
      };

      return { nodes: [...nodeList, mergeNode], mergeNodeId };
    },
    [getConditionMergeNodeId]
  );

  const cleanNodesForRedux = useCallback((nodesToClean: Node[]): Node[] => {
    const transientKeys = new Set([
      'isDragMode',
      'isNodeDragMode',
      'isDraggingNode',
      'isDraggedNode',
      'isAnyNodeDragging',
      'isTerminal',
      'isAddTarget',
      'activeAddBranch',
      'trueBranchTerminal',
      'falseBranchTerminal',
      'mergeIncomingBranches',
      'shouldShowAddAffordance',
    ]);

    return nodesToClean.map((node) => {
      const { measured, ...nodeWithoutMeasured } = node;
      const cleanedData = node.data
        ? Object.fromEntries(
            Object.entries(node.data).filter(([key]) => !transientKeys.has(key))
          )
        : node.data;
      return {
        ...nodeWithoutMeasured,
        data: cleanedData,
        position: node.position ? { ...node.position } : node.position,
      };
    });
  }, []);

  const getChangedNodeIds = useCallback(
    (prevNodes: Node[], nextNodes: Node[]) => {
      const prevIds = new Set(prevNodes.map((node) => node.id));
      const nextIds = new Set(nextNodes.map((node) => node.id));
      const changed = new Set<string>();

      prevIds.forEach((id) => {
        if (!nextIds.has(id)) changed.add(id);
      });
      nextIds.forEach((id) => {
        if (!prevIds.has(id)) changed.add(id);
      });
      return Array.from(changed);
    },
    []
  );

  const getChangedEdgeIds = useCallback(
    (prevEdges: Edge[], nextEdges: Edge[]) => {
      const prevIds = new Set(prevEdges.map((edge) => edge.id));
      const nextIds = new Set(nextEdges.map((edge) => edge.id));
      const changed = new Set<string>();

      prevIds.forEach((id) => {
        if (!nextIds.has(id)) changed.add(id);
      });
      nextIds.forEach((id) => {
        if (!prevIds.has(id)) changed.add(id);
      });
      return Array.from(changed);
    },
    []
  );

  const applyGraphState = useCallback(
    (
      rawNodes: Node[],
      rawEdges: Edge[],
      options: ApplyGraphStateOptions = {}
    ) => {
      const { persist = true, layoutHint } = options;
      const { nodes: normalizedNodes, edges: normalizedEdges } =
        normalizeWorkflowGraph(rawNodes, rawEdges);
      const changedNodeIds =
        layoutHint?.changedNodeIds || getChangedNodeIds(nodes, normalizedNodes);
      const changedEdgeIds =
        layoutHint?.changedEdgeIds || getChangedEdgeIds(edges, normalizedEdges);
      const { nodes: layoutedNodes, edges: layoutedEdges } =
        getLayoutedElements(normalizedNodes, normalizedEdges, 'TB', {
          operation: layoutHint?.operation || 'normalize',
          changedNodeIds,
          changedEdgeIds,
        });

      setNodes(layoutedNodes);
      setEdges(layoutedEdges);

      if (id && persist) {
        setTimeout(() => {
          const cleanedNodes = cleanNodesForRedux(layoutedNodes);
          dispatch(
            updateWorkflowRequest({
              campaignId: id,
              draftFlow: {
                nodes: cleanedNodes,
                edges: layoutedEdges,
              },
            })
          );
        }, 100);
      }
    },
    [
      cleanNodesForRedux,
      dispatch,
      edges,
      getChangedEdgeIds,
      getChangedNodeIds,
      id,
      nodes,
      setEdges,
      setNodes,
    ]
  );

  const onNodesChangeWithExitGuard = useCallback(
    (changes: NodeChange<Node>[]) => {
      const hasStructuralChange = changes.some(
        (change) => change.type === 'remove' || change.type === 'replace'
      );

      if (!hasStructuralChange) {
        onNodesChange(changes);
        return;
      }

      const exitNodeId =
        nodes.find((node) => node.data?.type === 'exit')?.id ?? null;
      const nodesWithoutExitDelete = changes.filter((change) => {
        if (change.type !== 'remove') return true;
        return change.id !== exitNodeId;
      });

      const updatedNodes = applyNodeChanges(nodesWithoutExitDelete, nodes);
      applyGraphState(updatedNodes, edges, {
        layoutHint: {
          operation: 'node-delete',
          changedNodeIds: changes
            .filter(
              (change): change is NodeChange<Node> & { id: string } =>
                'id' in change
            )
            .map((change) => change.id),
        },
      });
    },
    [nodes, edges, onNodesChange, applyGraphState]
  );

  const onEdgesChangeWithExitGuard = useCallback(
    (changes: EdgeChange<Edge>[]) => {
      const hasStructuralChange = changes.some(
        (change) => change.type === 'remove' || change.type === 'replace'
      );

      if (!hasStructuralChange) {
        onEdgesChange(changes);
        return;
      }

      const updatedEdges = applyEdgeChanges(changes, edges);
      applyGraphState(nodes, updatedEdges, {
        layoutHint: {
          operation: 'edge-delete',
          changedEdgeIds: changes
            .filter(
              (change): change is EdgeChange<Edge> & { id: string } =>
                'id' in change
            )
            .map((change) => change.id),
        },
      });
    },
    [nodes, edges, onEdgesChange, applyGraphState]
  );

  const addNewNode = useCallback(
    (
      nodeType: string,
      config: any,
      sourceId?: string,
      targetIdOrOptions?: string | { edgeId?: string | null }
    ) => {
      const options =
        typeof targetIdOrOptions === 'object' && targetIdOrOptions !== null
          ? targetIdOrOptions
          : undefined;
      const effectiveSelectedEdge =
        options?.edgeId != null
          ? edges.find((edge) => edge.id === options.edgeId) || null
          : selectedEdge;
      const newNode: Node<any, string | undefined> = {
        id: uuidv4(),
        type: 'action',
        position: { x: 0, y: 0 },
        data: {
          label: config.name || 'New Node',
          status: 'draft',
          type: nodeType,
          config: {
            conditions: {},
            inputs: {},
            outputs: {},
            settings: {},
          },
        },
      };
      if (effectiveSelectedEdge) {
        const edgeBranch = (
          effectiveSelectedEdge.data as
            | { branch?: 'true' | 'false' }
            | undefined
        )?.branch;
        const selectedEdgeSourceNode = nodes.find(
          (node) => node.id === effectiveSelectedEdge.source
        );
        const selectedEdgeTargetNode = nodes.find(
          (node) => node.id === effectiveSelectedEdge.target
        );
        const selectedEdgeTargetsOwnedMerge =
          selectedEdgeTargetNode?.data?.type === 'merge' &&
          selectedEdgeTargetNode?.data?.parentConditionId ===
            effectiveSelectedEdge.source;
        const isBranchPlaceholderEdge =
          selectedEdgeSourceNode?.data?.type === 'true-false' &&
          (effectiveSelectedEdge.sourceHandle === 'true' ||
            effectiveSelectedEdge.sourceHandle === 'false') &&
          selectedEdgeTargetsOwnedMerge;

        if (nodeType === 'true-false') {
          const conditionNodeId = newNode.id;
          const { nodes: nodesWithMerge, mergeNodeId } =
            ensureConditionMergeNode(conditionNodeId, [...nodes, newNode]);
          const incomingEdge = createEdge(
            effectiveSelectedEdge.source,
            conditionNodeId,
            {
              sourceHandle: effectiveSelectedEdge.sourceHandle ?? undefined,
              branch: edgeBranch,
            }
          );
          const trueEdge = createEdge(conditionNodeId, mergeNodeId, {
            sourceHandle: 'true',
            branch: 'true',
          });
          const falseEdge = createEdge(conditionNodeId, mergeNodeId, {
            sourceHandle: 'false',
            branch: 'false',
          });
          const mergeOutEdge = createEdge(
            mergeNodeId,
            effectiveSelectedEdge.target
          );

          if (incomingEdge && trueEdge && falseEdge && mergeOutEdge) {
            const filteredEdges = edges.filter(
              (edge: Edge) => edge.id !== effectiveSelectedEdge.id
            );
            const newEdges = [
              ...filteredEdges,
              incomingEdge,
              trueEdge,
              falseEdge,
              mergeOutEdge,
            ];
            applyGraphState(nodesWithMerge, newEdges, {
              layoutHint: {
                operation: 'node-add',
                changedNodeIds: [newNode.id, mergeNodeId],
                changedEdgeIds: [
                  incomingEdge.id,
                  trueEdge.id,
                  falseEdge.id,
                  mergeOutEdge.id,
                  effectiveSelectedEdge.id,
                ],
              },
            });
            setSelectedEdge(null);
          }
          return;
        }

        if (isBranchPlaceholderEdge) {
          const newBranchEdge = createEdge(
            effectiveSelectedEdge.source,
            newNode.id,
            {
              sourceHandle: effectiveSelectedEdge.sourceHandle ?? undefined,
              branch: edgeBranch,
            }
          );
          const mergeReconnectEdge = createEdge(
            newNode.id,
            effectiveSelectedEdge.target
          );
          if (newBranchEdge && mergeReconnectEdge) {
            const filteredEdges = edges.filter(
              (edge: Edge) => edge.id !== effectiveSelectedEdge.id
            );
            const newEdges = [
              ...filteredEdges,
              newBranchEdge,
              mergeReconnectEdge,
            ];
            const newNodes = [...nodes, newNode];
            applyGraphState(newNodes, newEdges, {
              layoutHint: {
                operation: 'node-add',
                changedNodeIds: [
                  newNode.id,
                  effectiveSelectedEdge.source,
                  effectiveSelectedEdge.target,
                ],
                changedEdgeIds: [
                  newBranchEdge.id,
                  mergeReconnectEdge.id,
                  effectiveSelectedEdge.id,
                ],
              },
            });
            setSelectedEdge(null);
          }
          return;
        }

        const newEdge1 = createEdge(effectiveSelectedEdge.source, newNode.id, {
          sourceHandle: effectiveSelectedEdge.sourceHandle ?? undefined,
          branch: edgeBranch,
        });
        const newEdge2 = createEdge(newNode.id, effectiveSelectedEdge.target);
        if (newEdge1 && newEdge2) {
          const filteredEdges = edges.filter(
            (edge: Edge) => edge.id !== effectiveSelectedEdge.id
          );
          const newEdges = [...filteredEdges, newEdge1, newEdge2];
          const newNodes = [...nodes, newNode];
          const changedEdgeIds = [
            newEdge1.id,
            newEdge2.id,
            effectiveSelectedEdge.id,
          ];
          const changedNodeIds = [newNode.id];

          applyGraphState(newNodes, newEdges, {
            layoutHint: {
              operation: 'node-add',
              changedNodeIds,
              changedEdgeIds,
            },
          });
          setSelectedEdge(null);
        }
      } else {
        const targetSourceId = sourceId || addAfterNode?.nodeId;
        const targetBranch = addAfterNode?.branch ?? undefined;
        const lastEdge = edges.length > 0 ? edges[edges.length - 1] : null;
        const lastEdgeTarget = lastEdge?.target;
        const fallbackLastNode =
          nodes.length > 1
            ? nodes.find((node) => node.id === lastEdgeTarget)
            : nodes[0];
        const sourceNode = targetSourceId
          ? nodes.find((node) => node.id === targetSourceId)
          : fallbackLastNode;
        if (
          sourceNode?.data?.type === 'true-false' &&
          targetBranch !== 'true' &&
          targetBranch !== 'false'
        ) {
          return;
        }
        const newEdge = createEdge(sourceNode?.id || '', newNode.id, {
          sourceHandle: targetBranch,
          branch: targetBranch,
        });
        let updatedNodes: Node[] = [];
        let updatedEdges: Edge[] = [];
        if (newEdge) {
          const sourceIsCondition = sourceNode?.data?.type === 'true-false';
          const branchHandle =
            targetBranch === 'true' || targetBranch === 'false';
          const existingBranchEdge =
            sourceIsCondition && branchHandle
              ? edges.find(
                  (edge) =>
                    edge.source === sourceNode?.id &&
                    edge.sourceHandle === targetBranch
                )
              : null;
          const existingBranchTargetNode = existingBranchEdge
            ? nodes.find((node) => node.id === existingBranchEdge.target)
            : null;
          const pointsToOwnedMerge =
            !!existingBranchEdge &&
            existingBranchTargetNode?.data?.type === 'merge' &&
            existingBranchTargetNode?.data?.parentConditionId ===
              sourceNode?.id;

          const filteredEdges = edges.filter((edge: Edge) => {
            if (
              edge.source === sourceNode?.id &&
              edge.target === newNode.id &&
              edge.sourceHandle === targetBranch
            ) {
              return false;
            }
            if (pointsToOwnedMerge && edge.id === existingBranchEdge?.id) {
              return false;
            }
            return true;
          });

          updatedEdges = [...filteredEdges, newEdge];
          updatedNodes = [...nodes, newNode];

          if (
            pointsToOwnedMerge &&
            existingBranchTargetNode &&
            existingBranchTargetNode.id !== newNode.id
          ) {
            const reconnectToMerge = createEdge(
              newNode.id,
              existingBranchTargetNode.id
            );
            if (reconnectToMerge) {
              updatedEdges.push(reconnectToMerge);
            }
          }

          if (nodeType === 'true-false') {
            const withMerge = ensureConditionMergeNode(
              newNode.id,
              updatedNodes
            );
            const trueEdge = createEdge(newNode.id, withMerge.mergeNodeId, {
              sourceHandle: 'true',
              branch: 'true',
            });
            const falseEdge = createEdge(newNode.id, withMerge.mergeNodeId, {
              sourceHandle: 'false',
              branch: 'false',
            });
            if (trueEdge && falseEdge) {
              updatedNodes = withMerge.nodes;
              updatedEdges.push(trueEdge, falseEdge);
            }
          }

          applyGraphState(updatedNodes, updatedEdges, {
            layoutHint: {
              operation: 'node-add',
              changedNodeIds: [
                newNode.id,
                ...(sourceNode?.id ? [sourceNode.id] : []),
                ...(nodeType === 'true-false'
                  ? [getConditionMergeNodeId(newNode.id, updatedNodes)].filter(
                      (value): value is string => !!value
                    )
                  : []),
              ],
              changedEdgeIds: updatedEdges.map((edge) => edge.id),
            },
          });
          setAddAfterNode(null);
        }
      }
    },
    [
      createEdge,
      selectedEdge,
      nodes,
      edges,
      applyGraphState,
      addAfterNode,
      ensureConditionMergeNode,
      getConditionMergeNodeId,
    ]
  );

  const parseDragPayload = useCallback(
    (event: React.DragEvent): WorkflowPaletteDropPayload | null => {
      const payload = event.dataTransfer.getData(WORKFLOW_DND_MIME);
      if (!payload) return null;
      try {
        const parsed = JSON.parse(payload) as WorkflowPaletteDropPayload;
        if (!parsed?.nodeType || !parsed?.config) return null;
        return parsed;
      } catch {
        return null;
      }
    },
    []
  );

  const clearDragState = useCallback(() => {
    setIsPaletteDragging((prev) => (prev ? false : prev));
    updateDropCandidateEdgeId(null);
    setAppendMode((prev) => (prev ? false : prev));
  }, [updateDropCandidateEdgeId]);

  const onPaletteDragOver = useCallback(
    (event: React.DragEvent) => {
      const hasPayload = event.dataTransfer.types.includes(WORKFLOW_DND_MIME);
      if (!hasPayload) return;
      event.preventDefault();
      setIsPaletteDragging((prev) => (prev ? prev : true));
      const isAppendMode = event.shiftKey;
      setAppendMode((prev) => (prev === isAppendMode ? prev : isAppendMode));

      if (!reactFlowInstance || isAppendMode) {
        updateDropCandidateEdgeId(null);
        event.dataTransfer.dropEffect = 'copy';
        return;
      }

      const flowPoint = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });
      const nearest = findNearestWorkflowEdge(flowPoint, nodes, edges);
      updateDropCandidateEdgeId(nearest?.edge?.id ?? null);
      event.dataTransfer.dropEffect = nearest ? 'copy' : 'none';
    },
    [edges, nodes, reactFlowInstance, updateDropCandidateEdgeId]
  );

  const onPaletteDrop = useCallback(
    (event: React.DragEvent) => {
      const payload = parseDragPayload(event);
      if (!payload) {
        clearDragState();
        return;
      }
      event.preventDefault();

      const edgeId = !appendMode ? dropCandidateEdgeIdRef.current : null;
      if (!appendMode && !edgeId) {
        clearDragState();
        return;
      }
      if (edgeId) {
        addNewNode(payload.nodeType, payload.config, undefined, { edgeId });
      } else {
        addNewNode(payload.nodeType, payload.config);
      }
      clearDragState();
    },
    [addNewNode, appendMode, clearDragState, parseDragPayload]
  );

  const persistNodePositions = useCallback(
    (nextNodes: Node[]) => {
      if (!id) return;
      const cleanedNodes = cleanNodesForRedux(nextNodes);
      dispatch(
        updateWorkflowRequest({
          campaignId: id,
          draftFlow: {
            nodes: cleanedNodes,
            edges,
          },
        })
      );
    },
    [cleanNodesForRedux, dispatch, edges, id]
  );

  const getConditionBlockNodeIds = useCallback(
    (conditionNodeId: string, mergeNodeId: string) => {
      const ids = new Set<string>([conditionNodeId]);
      const queue: string[] = [conditionNodeId];
      while (queue.length > 0) {
        const current = queue.shift()!;
        if (current === mergeNodeId) continue;
        edges.forEach((edge) => {
          if (edge.source !== current) return;
          if (ids.has(edge.target)) return;
          ids.add(edge.target);
          queue.push(edge.target);
        });
      }
      return ids;
    },
    [edges]
  );

  const getDragGroupNodeIds = useCallback(
    (node: Node, currentNodes: Node[]): Set<string> => {
      const nodeType = (node.data as { type?: string } | undefined)?.type;
      if (nodeType === 'true-false') {
        const ownedMergeNode = currentNodes.find(
          (candidate) =>
            candidate.data?.type === 'merge' &&
            (candidate.data as MergeNodeData | undefined)?.parentConditionId ===
              node.id
        );
        if (ownedMergeNode) {
          const blockIds = getConditionBlockNodeIds(node.id, ownedMergeNode.id);
          const merged = new Set<string>(blockIds);
          merged.add(ownedMergeNode.id);
          return merged;
        }
      }
      return new Set<string>([node.id]);
    },
    [getConditionBlockNodeIds]
  );

  const moveNodeToEdge = useCallback(
    (nodeId: string, targetEdgeId: string) => {
      const movingNode = nodes.find((node) => node.id === nodeId);
      const targetEdge = edges.find((edge) => edge.id === targetEdgeId);
      if (!movingNode || !targetEdge) return false;

      const movingType = (movingNode.data as any)?.type;
      if (
        movingType === 'trigger' ||
        movingType === 'merge' ||
        movingType === 'exit'
      ) {
        return false;
      }

      if (movingType === 'true-false') {
        const ownedMergeNode = nodes.find(
          (node) =>
            node.data?.type === 'merge' &&
            (node.data as MergeNodeData | undefined)?.parentConditionId ===
              nodeId
        );
        if (!ownedMergeNode) return false;

        const blockNodeIds = getConditionBlockNodeIds(
          nodeId,
          ownedMergeNode.id
        );
        if (
          blockNodeIds.has(targetEdge.source) ||
          blockNodeIds.has(targetEdge.target)
        ) {
          return false;
        }

        const incomingToCondition = edges.filter(
          (edge) => edge.target === nodeId
        );
        const continuationFromMerge = edges.filter(
          (edge) =>
            edge.source === ownedMergeNode.id && !blockNodeIds.has(edge.target)
        );
        if (
          incomingToCondition.length === 0 ||
          continuationFromMerge.length === 0
        ) {
          return false;
        }

        const removedEdgeIds = new Set<string>([
          targetEdge.id,
          ...incomingToCondition.map((edge) => edge.id),
          ...continuationFromMerge.map((edge) => edge.id),
        ]);
        const remainingEdges = edges.filter(
          (edge) => !removedEdgeIds.has(edge.id)
        );

        const reconnectOldPathEdges: Edge[] = [];
        incomingToCondition.forEach((incoming) => {
          continuationFromMerge.forEach((continuation) => {
            reconnectOldPathEdges.push({
              id: uuidv4(),
              source: incoming.source,
              target: continuation.target,
              type: 'custom',
              sourceHandle: incoming.sourceHandle,
              data:
                (incoming.data as { branch?: 'true' | 'false' } | undefined)
                  ?.branch != null
                  ? {
                      branch: (
                        incoming.data as
                          | { branch?: 'true' | 'false' }
                          | undefined
                      )?.branch,
                    }
                  : undefined,
            });
          });
        });

        const insertInEdge: Edge = {
          id: uuidv4(),
          source: targetEdge.source,
          target: nodeId,
          type: 'custom',
          sourceHandle: targetEdge.sourceHandle,
          data:
            (targetEdge.data as { branch?: 'true' | 'false' } | undefined)
              ?.branch != null
              ? {
                  branch: (
                    targetEdge.data as { branch?: 'true' | 'false' } | undefined
                  )?.branch,
                }
              : undefined,
        };
        const insertOutEdge: Edge = {
          id: uuidv4(),
          source: ownedMergeNode.id,
          target: targetEdge.target,
          type: 'custom',
        };

        const nextEdges = [
          ...remainingEdges,
          ...reconnectOldPathEdges,
          insertInEdge,
          insertOutEdge,
        ];

        applyGraphState(nodes, nextEdges, {
          layoutHint: {
            operation: 'node-update',
            changedNodeIds: Array.from(
              new Set([
                nodeId,
                ownedMergeNode.id,
                targetEdge.source,
                targetEdge.target,
              ])
            ),
          },
        });
        return true;
      }

      const incomingEdges = edges.filter((edge) => edge.target === nodeId);
      const outgoingEdges = edges.filter((edge) => edge.source === nodeId);
      if (incomingEdges.length === 0 || outgoingEdges.length === 0)
        return false;
      if (targetEdge.source === nodeId || targetEdge.target === nodeId)
        return false;

      const removedEdgeIds = new Set<string>([
        targetEdge.id,
        ...incomingEdges.map((edge) => edge.id),
        ...outgoingEdges.map((edge) => edge.id),
      ]);
      const remainingEdges = edges.filter(
        (edge) => !removedEdgeIds.has(edge.id)
      );

      const reconnectOldPathEdges: Edge[] = [];
      incomingEdges.forEach((incoming) => {
        outgoingEdges.forEach((outgoing) => {
          if (incoming.source === outgoing.target) return;
          reconnectOldPathEdges.push({
            id: uuidv4(),
            source: incoming.source,
            target: outgoing.target,
            type: 'custom',
            sourceHandle: incoming.sourceHandle,
            data:
              (incoming.data as { branch?: 'true' | 'false' } | undefined)
                ?.branch != null
                ? {
                    branch: (
                      incoming.data as { branch?: 'true' | 'false' } | undefined
                    )?.branch,
                  }
                : undefined,
          });
        });
      });

      const movedInEdge: Edge = {
        id: uuidv4(),
        source: targetEdge.source,
        target: nodeId,
        type: 'custom',
        sourceHandle: targetEdge.sourceHandle,
        data:
          (targetEdge.data as { branch?: 'true' | 'false' } | undefined)
            ?.branch != null
            ? {
                branch: (
                  targetEdge.data as { branch?: 'true' | 'false' } | undefined
                )?.branch,
              }
            : undefined,
      };
      const movedOutEdge: Edge = {
        id: uuidv4(),
        source: nodeId,
        target: targetEdge.target,
        type: 'custom',
      };

      const nextEdges = [
        ...remainingEdges,
        ...reconnectOldPathEdges,
        movedInEdge,
        movedOutEdge,
      ];

      applyGraphState(nodes, nextEdges, {
        layoutHint: {
          operation: 'node-update',
          changedNodeIds: [nodeId, targetEdge.source, targetEdge.target],
        },
      });
      return true;
    },
    [applyGraphState, edges, getConditionBlockNodeIds, nodes]
  );

  const onNodeDragStart = useCallback(
    (_event: React.MouseEvent, node: Node) => {
      setIsNodeDragging((prev) => (prev ? prev : true));
      updateNodeDragCandidateEdgeId(null);
      const nodeType = (node.data as { type?: string } | undefined)?.type;
      if (nodeType === 'true-false') {
        const ownedMergeNode = nodes.find(
          (candidate) =>
            candidate.data?.type === 'merge' &&
            (candidate.data as MergeNodeData | undefined)?.parentConditionId ===
              node.id
        );
        nodeDragExclusionRef.current = ownedMergeNode
          ? getConditionBlockNodeIds(node.id, ownedMergeNode.id)
          : new Set([node.id]);
      } else {
        nodeDragExclusionRef.current = new Set([node.id]);
      }
      const groupIds = getDragGroupNodeIds(node, nodes);
      setDraggingGroupNodeIds(groupIds);
      const startPositions: Record<string, { x: number; y: number }> = {};
      nodes.forEach((n) => {
        if (groupIds.has(n.id)) {
          startPositions[n.id] = { ...n.position };
        }
      });
      dragGroupDragRef.current = {
        primaryId: node.id,
        startPositions,
      };
      setDragSkeletonSize({
        width: (node.measured?.width ?? 300) + 10,
        height: (node.measured?.height ?? 120) + 10,
      });
      setSelectedNode(node);
    },
    [
      getConditionBlockNodeIds,
      getDragGroupNodeIds,
      nodes,
      updateNodeDragCandidateEdgeId,
    ]
  );

  const onNodeDrag = useCallback(
    (_event: React.MouseEvent, node: Node) => {
      const snapshot = dragGroupDragRef.current;
      let nodesForHitTest = nodes;

      if (snapshot) {
        const { primaryId, startPositions } = snapshot;
        const primaryStart = startPositions[primaryId];
        if (primaryStart) {
          const delta = {
            x: node.position.x - primaryStart.x,
            y: node.position.y - primaryStart.y,
          };
          nodesForHitTest = nodes.map((n) => {
            if (!startPositions[n.id]) return n;
            return {
              ...n,
              position: {
                x: startPositions[n.id].x + delta.x,
                y: startPositions[n.id].y + delta.y,
              },
            };
          });
          setNodes(nodesForHitTest);
        }
      }

      const nodeWidth = node.measured?.width ?? 300;
      const nodeHeight = node.measured?.height ?? 120;
      const point = {
        x: node.position.x + nodeWidth / 2,
        y: node.position.y + nodeHeight / 2,
      };
      const nearest = findNearestWorkflowEdge(
        point,
        nodesForHitTest,
        edges,
        110,
        nodeDragExclusionRef.current
      );
      updateNodeDragCandidateEdgeId(nearest?.edge?.id ?? null);
    },
    [edges, nodes, setNodes, updateNodeDragCandidateEdgeId]
  );

  const onNodeDragStop = useCallback(
    (_event: React.MouseEvent, node: Node, nextNodes: Node[]) => {
      setIsNodeDragging((prev) => (prev ? false : prev));
      setDraggingGroupNodeIds(null);
      dragGroupDragRef.current = null;
      const candidateEdgeId = nodeDragCandidateEdgeIdRef.current;
      updateNodeDragCandidateEdgeId(null);
      setDragSkeletonSize((prev) =>
        prev.width === 310 && prev.height === 130
          ? prev
          : { width: 310, height: 130 }
      );
      const nodeList = nextNodes || nodes;
      const movedNode = nodeList.find((n) => n.id === node.id) || node;
      setSelectedNode(movedNode);
      if (candidateEdgeId) {
        const rewired = moveNodeToEdge(node.id, candidateEdgeId);
        if (!rewired) {
          console.log('rewired', nodeList);
          persistNodePositions(nodeList);
        }
      }
    },
    [moveNodeToEdge, nodes, persistNodePositions, updateNodeDragCandidateEdgeId]
  );

  const deleteSelectedNode = useCallback(() => {
    if (
      !selectedNode ||
      selectedNode.data?.isDefault ||
      selectedNode.data?.type === 'merge'
    ) {
      return;
    }

    if (selectedNode.data?.type === 'true-false') {
      const ownedMergeNode = nodes.find(
        (node) =>
          node.data?.type === 'merge' &&
          (node.data as MergeNodeData | undefined)?.parentConditionId ===
            selectedNode.id
      );
      const ownedMergeId = ownedMergeNode?.id;
      const incomingEdges = edges.filter(
        (edge) => edge.target === selectedNode.id
      );
      const outgoingEdgesFromCondition = edges.filter(
        (edge) => edge.source === selectedNode.id
      );

      const idsToRemove = new Set<string>([selectedNode.id]);
      const queue: string[] = [];
      const seeded = new Set<string>();
      outgoingEdgesFromCondition.forEach((edge) => {
        if (!seeded.has(edge.target)) {
          queue.push(edge.target);
          seeded.add(edge.target);
        }
      });

      while (queue.length > 0) {
        const currentId = queue.shift()!;
        if (idsToRemove.has(currentId)) continue;
        if (ownedMergeId && currentId === ownedMergeId) continue;
        idsToRemove.add(currentId);
        edges.forEach((edge) => {
          if (edge.source === currentId) {
            queue.push(edge.target);
          }
        });
      }

      if (ownedMergeId) {
        idsToRemove.add(ownedMergeId);
      }

      const continuationEdges = ownedMergeId
        ? edges.filter((edge) => edge.source === ownedMergeId)
        : [];

      const edgesWithoutDeleted = edges.filter(
        (edge) => !idsToRemove.has(edge.source) && !idsToRemove.has(edge.target)
      );
      const reconnectEdges: Edge[] = [];

      incomingEdges.forEach((incomingEdge) => {
        continuationEdges.forEach((continuationEdge) => {
          const edgeBranch = (
            incomingEdge.data as { branch?: 'true' | 'false' } | undefined
          )?.branch;
          const createdEdge = createEdge(
            incomingEdge.source,
            continuationEdge.target,
            {
              sourceHandle: incomingEdge.sourceHandle ?? undefined,
              branch: edgeBranch,
            }
          );
          if (createdEdge) {
            reconnectEdges.push(createdEdge as Edge);
          }
        });
      });

      const dedupedReconnectEdges = reconnectEdges.filter(
        (edge, index, self) =>
          self.findIndex(
            (e) =>
              e.source === edge.source &&
              e.target === edge.target &&
              e.sourceHandle === edge.sourceHandle
          ) === index
      );

      const finalEdges = [...edgesWithoutDeleted, ...dedupedReconnectEdges];
      const finalNodes = nodes.filter((node) => !idsToRemove.has(node.id));

      applyGraphState(finalNodes, finalEdges, {
        layoutHint: {
          operation: 'node-delete',
          changedNodeIds: Array.from(idsToRemove),
        },
      });
      setSelectedNode(null);
      clearReactFlowSelection();
      return;
    }

    const incomingEdges = edges.filter(
      (edge) => edge.target === selectedNode.id
    );
    const outgoingEdges = edges.filter(
      (edge) => edge.source === selectedNode.id
    );
    const edgesWithoutSelectedNode = edges.filter(
      (edge) =>
        edge.source !== selectedNode.id && edge.target !== selectedNode.id
    );

    const reconnectedEdges: Edge[] = [];
    incomingEdges.forEach((incomingEdge) => {
      outgoingEdges.forEach((outgoingEdge) => {
        const edgeBranch = (
          incomingEdge.data as { branch?: 'true' | 'false' } | undefined
        )?.branch;

        const createdEdge = createEdge(
          incomingEdge.source,
          outgoingEdge.target,
          {
            sourceHandle: incomingEdge.sourceHandle ?? undefined,
            branch: edgeBranch,
          }
        );

        if (createdEdge) {
          reconnectedEdges.push(createdEdge as Edge);
        }
      });
    });

    const dedupedReconnectedEdges = reconnectedEdges.filter(
      (edge, index, self) =>
        self.findIndex(
          (e) =>
            e.source === edge.source &&
            e.target === edge.target &&
            e.sourceHandle === edge.sourceHandle
        ) === index
    );

    const finalEdges = [
      ...edgesWithoutSelectedNode,
      ...dedupedReconnectedEdges,
    ];
    const finalNodes = nodes.filter((node) => node.id !== selectedNode.id);

    applyGraphState(finalNodes, finalEdges, {
      layoutHint: {
        operation: 'node-delete',
        changedNodeIds: [selectedNode.id],
      },
    });

    setSelectedNode(null);
    clearReactFlowSelection();
  }, [
    selectedNode,
    createEdge,
    clearReactFlowSelection,
    nodes,
    edges,
    applyGraphState,
  ]);

  const updateNodeConfig = useCallback(
    (nodeId: string, config: Record<string, any>) => {
      const updatedNodes = nodes.map((node) => {
        if (node.id === nodeId) {
          return {
            ...node,
            data: {
              ...node.data,
              config: {
                ...(node.data.config || {}),
                ...config,
              },
            },
          };
        }
        return node;
      });

      applyGraphState(updatedNodes, edges, {
        layoutHint: {
          operation: 'node-update',
          changedNodeIds: [nodeId],
        },
      });
    },
    [nodes, edges, applyGraphState]
  );

  useEffect(() => {
    if (id) {
      dispatch(getAttributesRequest());
      dispatch(getSegmentsRequest({}));
      dispatch(getWorkflowByCampaignRequest(id));
      dispatch(reviewCampaignRequest(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        clearSelectionState();
        setReviewPanelMode(null);
        clearReactFlowSelection();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [clearReactFlowSelection, clearSelectionState]);

  useEffect(() => {
    const handleAddNodeAfterNode = (event: Event) => {
      const customEvent = event as CustomEvent<{
        nodeId: string;
        branch?: 'true' | 'false' | null;
      }>;
      const nodeId = customEvent.detail?.nodeId;
      if (!nodeId) return;
      setAddAfterNode({
        nodeId,
        branch: customEvent.detail?.branch || null,
      });
      setSelectedNode(null);
      setSelectedEdge(null);
      clearReactFlowSelection();
    };

    window.addEventListener(
      'addNodeAfterNode',
      handleAddNodeAfterNode as EventListener
    );

    return () => {
      window.removeEventListener(
        'addNodeAfterNode',
        handleAddNodeAfterNode as EventListener
      );
    };
  }, [clearReactFlowSelection]);

  useEffect(() => {
    const handleAddNodeFromEdge = (event: Event) => {
      const customEvent = event as CustomEvent<{ edgeId: string }>;
      const edgeId = customEvent.detail?.edgeId;
      if (!edgeId) return;
      const edge = edges.find((item) => item.id === edgeId) || null;
      setSelectedEdge(edge);
      setSelectedNode(null);
      setAddAfterNode(null);
      clearReactFlowSelection();
    };

    window.addEventListener(
      'addNodeFromEdge',
      handleAddNodeFromEdge as EventListener
    );
    return () => {
      window.removeEventListener(
        'addNodeFromEdge',
        handleAddNodeFromEdge as EventListener
      );
    };
  }, [edges, clearReactFlowSelection]);

  const preferredDefaultEdge = useMemo(() => {
    if (edges.length === 0) return null;

    const exitNode = nodes.find((node) => (node.data as any)?.type === 'exit');
    if (!exitNode) return edges[edges.length - 1];

    let lastIncomingToExit: Edge | null = null;
    let trueBranchToExit: Edge | null = null;

    for (let i = 0; i < edges.length; i++) {
      const edge = edges[i];
      if (edge.target !== exitNode.id) continue;
      lastIncomingToExit = edge;
      if (edge.sourceHandle === 'true' && !trueBranchToExit) {
        trueBranchToExit = edge;
      }
    }

    if (!lastIncomingToExit) return edges[edges.length - 1];
    return trueBranchToExit ?? lastIncomingToExit;
  }, [nodes, edges]);

  useEffect(() => {
    if (selectedNode || addAfterNode || reviewPanelMode) return;

    if (!preferredDefaultEdge) {
      if (selectedEdge) setSelectedEdge(null);
      return;
    }

    if (selectedEdge && edges.some((edge) => edge.id === selectedEdge.id)) {
      return;
    }

    setSelectedEdge(preferredDefaultEdge);
  }, [
    edges,
    selectedEdge,
    selectedNode,
    addAfterNode,
    reviewPanelMode,
    preferredDefaultEdge,
  ]);

  const renderEdges = useMemo(
    () =>
      edges.map((edge) => {
        const sourceInGroup = draggingGroupNodeIds?.has(edge.source) ?? false;
        const targetInGroup = draggingGroupNodeIds?.has(edge.target) ?? false;
        const edgeCrossesDragGroup = sourceInGroup !== targetInGroup;
        return {
          ...edge,
          data: {
            ...(edge.data || {}),
            isPreferred: selectedEdge?.id === edge.id,
            isDraggingPalette: isPaletteDragging,
            isDropCandidate: !appendMode && dropCandidateEdgeId === edge.id,
            isNodeDragCandidate:
              isNodeDragging && nodeDragCandidateEdgeId === edge.id,
            hideWhileDraggingIncident:
              isNodeDragging &&
              draggingGroupNodeIds !== null &&
              draggingGroupNodeIds.size > 0 &&
              edgeCrossesDragGroup,
            shouldDeemphasize:
              (isPaletteDragging &&
                (!appendMode
                  ? dropCandidateEdgeId !== null &&
                    dropCandidateEdgeId !== edge.id
                  : true)) ||
              (isNodeDragging &&
                nodeDragCandidateEdgeId !== null &&
                nodeDragCandidateEdgeId !== edge.id),
            isNodeDragMode: isNodeDragging,
            skeletonWidth: dragSkeletonSize.width,
            skeletonHeight: dragSkeletonSize.height,
          },
        };
      }),
    [
      appendMode,
      draggingGroupNodeIds,
      dropCandidateEdgeId,
      edges,
      isNodeDragging,
      nodeDragCandidateEdgeId,
      dragSkeletonSize.height,
      dragSkeletonSize.width,
      isPaletteDragging,
      selectedEdge?.id,
    ]
  );

  const nodeDragCandidateEdge = useMemo(
    () =>
      nodeDragCandidateEdgeId
        ? edges.find((edge) => edge.id === nodeDragCandidateEdgeId) || null
        : null,
    [edges, nodeDragCandidateEdgeId]
  );

  const onSelectionChange = useCallback(
    (selection: { nodes: Node[]; edges: Edge[] }) => {
      const selectedNodeData = selection.nodes[0] as Node | undefined;
      setSelectedNode(selectedNodeData || null);
      setSelectedEdge((selection.edges[0] as Edge) || null);
      setAddAfterNode(null);
      if (selectedNodeData) {
        setReviewPanelMode(null);
        if (selectedNodeData?.data?.type) {
          const existingConditions =
            (selectedNodeData.data?.config as any)?.conditions || {};
          setNodeConditions(existingConditions);
          setNodeConditionsModified(false);
        }
      }
    },
    []
  );

  const onPaneClick = useCallback(() => {
    setReviewPanelMode(null);
    clearSelectionState();
    clearReactFlowSelection();
  }, [clearReactFlowSelection, clearSelectionState]);

  const handleCloseSelectedNodePanel = useCallback(() => {
    setSelectedNode(null);
    setNodeConditionsModified(false);
    clearReactFlowSelection();
  }, [clearReactFlowSelection]);

  const handleSaveSelectedNode = useCallback(
    (
      data: Record<string, any>,
      options?: { updateStatus?: boolean; close?: boolean }
    ) => {
      if (!selectedNode) return;
      const { updateStatus = false, close = true } = options || {};

      updateNodeConfig(selectedNode.id, data);
      if (updateStatus) {
        setNodes((nds) =>
          nds.map((node) => {
            if (node.id !== selectedNode.id) return node;
            return {
              ...node,
              data: {
                ...node.data,
                status: data.status || 'configured',
              },
            };
          })
        );
      }
      setNodeConditions(data.conditions || {});
      setNodeConditionsModified(false);
      if (close) {
        setSelectedNode(null);
      }
      clearReactFlowSelection();
    },
    [clearReactFlowSelection, selectedNode, setNodes, updateNodeConfig]
  );

  const connectionLineStyle = useMemo(
    () => ({
      stroke: 'hsl(var(--primary))',
      strokeWidth: 2,
    }),
    []
  );

  const nodeColor = useCallback((node: any) => {
    switch (node.type) {
      case 'action':
        return 'hsl(var(--primary))';
      default:
        return 'hsl(var(--muted-foreground))';
    }
  }, []);

  const renderNodes = useMemo(() => {
    const activeNodeAddTarget = selectedEdge ? null : addAfterNode;

    return nodes.map((node) => ({
      ...node,
      data: {
        ...(node.data || {}),
        isTerminal: !graphBranchMeta.sourceNodeIds.has(node.id),
        trueBranchTerminal: !graphBranchMeta.trueBranchSources.has(node.id),
        falseBranchTerminal: !graphBranchMeta.falseBranchSources.has(node.id),
        isAddTarget: activeNodeAddTarget?.nodeId === node.id,
        activeAddBranch:
          activeNodeAddTarget?.nodeId === node.id
            ? activeNodeAddTarget?.branch || null
            : null,
        mergeIncomingBranches: graphBranchMeta.mergeIncomingNodeIds.has(
          node.id
        ),
        isDragMode: isPaletteDragging,
        isNodeDragMode: isNodeDragging,
        isDraggingNode: draggingGroupNodeIds?.has(node.id) ?? false,
        shouldShowAddAffordance:
          (isPaletteDragging || !!activeNodeAddTarget) && !isNodeDragging,
        shouldCreateSkeletonGap:
          isNodeDragging &&
          !!nodeDragCandidateEdge &&
          (node.id === nodeDragCandidateEdge.source ||
            node.id === nodeDragCandidateEdge.target) &&
          !draggingGroupNodeIds?.has(node.id),
        skeletonGapDirection:
          isNodeDragging &&
          !!nodeDragCandidateEdge &&
          !draggingGroupNodeIds?.has(node.id)
            ? node.id === nodeDragCandidateEdge.source
              ? 'up'
              : node.id === nodeDragCandidateEdge.target
                ? 'down'
                : null
            : null,
      },
    }));
  }, [
    addAfterNode,
    draggingGroupNodeIds,
    graphBranchMeta,
    isNodeDragging,
    isPaletteDragging,
    nodeDragCandidateEdge,
    nodes,
    selectedEdge,
  ]);

  return (
    <div className="h-[calc(90vh-35px)] w-full relative">
      {isLoadingWorkflow && (
        <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center z-[9999]">
          <Loader size="medium" showText text="Loading workflow..." />
        </div>
      )}
      <ReactFlow
        nodes={renderNodes}
        edges={renderEdges}
        onNodesChange={onNodesChangeWithExitGuard}
        onEdgesChange={onEdgesChangeWithExitGuard}
        onInit={setReactFlowInstance}
        onSelectionChange={onSelectionChange}
        onPaneClick={onPaneClick}
        onNodeDragStart={onNodeDragStart}
        onNodeDrag={onNodeDrag}
        onNodeDragStop={onNodeDragStop}
        onDragOver={onPaletteDragOver}
        onDrop={onPaletteDrop}
        onDragLeave={() => {
          if (!isPaletteDragging) return;
          clearDragState();
        }}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView={!isLoadingWorkflow && nodes.length > 0}
        fitViewOptions={{
          padding: 0.5,
          minZoom: 0.5,
          maxZoom: 1.2,
          duration: 1000,
        }}
        minZoom={0.5}
        maxZoom={1.2}
        snapToGrid
        nodesDraggable
        elementsSelectable={true}
        connectionLineStyle={connectionLineStyle}
        deleteKeyCode={null}
        className={
          isPaletteDragging
            ? 'cursor-copy'
            : isNodeDragging
              ? 'cursor-grabbing'
              : undefined
        }
      >
        <Controls />
        <Background color="hsl(var(--border))" gap={16} size={2.5} />
        <MiniMap nodeColor={nodeColor} nodeStrokeWidth={3} zoomable pannable />

        <Panel
          position="top-left"
          style={{ top: '0px', backgroundColor: 'transparent' }}
        >
          <div className="space-y-2">
            {(reviews?.errors?.length ?? 0) > 0 ||
            (reviews?.warnings?.length ?? 0) > 0 ? (
              <div className="p-1 flex flex-wrap gap-1 w-full">
                {reviews?.errors && reviews.errors.length > 0 && (
                  <button
                    onClick={() => {
                      setReviewPanelMode(
                        reviewPanelMode === 'errors' ? null : 'errors'
                      );
                      setSelectedNode(null);
                      clearReactFlowSelection();
                    }}
                    disabled={reviewLoading}
                    className={`flex items-center justify-center p-2 whitespace-nowrap border rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                      reviewPanelMode === 'errors'
                        ? 'bg-primary border-primary text-white'
                        : 'bg-white border-primary text-primary hover:bg-primary/10'
                    }`}
                  >
                    Review Errors ({reviews.errors.length})
                  </button>
                )}
                {reviews?.warnings && reviews.warnings.length > 0 && (
                  <button
                    onClick={() => {
                      setReviewPanelMode(
                        reviewPanelMode === 'warnings' ? null : 'warnings'
                      );
                      setSelectedNode(null);
                      clearReactFlowSelection();
                    }}
                    disabled={reviewLoading}
                    className={`flex items-center justify-center p-2 whitespace-nowrap border rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed ${
                      reviewPanelMode === 'warnings'
                        ? 'bg-amber-500 border-amber-500 text-white'
                        : 'bg-white border-amber-500/60 text-amber-500 dark:text-amber-400 hover:bg-amber-500/10'
                    }`}
                  >
                    Warnings ({reviews.warnings.length})
                  </button>
                )}
              </div>
            ) : null}
            {reviewPanelMode && (
              <ReviewErrorsPanel
                variant={reviewPanelMode}
                onClose={() => setReviewPanelMode(null)}
                campaignId={id || ''}
                onViewMessage={(nodeId) => {
                  const node = nodes.find((node: any) => node.id === nodeId);
                  if (node) {
                    setSelectedNode(node);
                    setReviewPanelMode(null);
                  }
                }}
              />
            )}
          </div>
        </Panel>

        <Panel position="top-right" style={{ top: '0px' }}>
          {!selectedNode && (
            <RightSidePanel
              addNewNode={addNewNode}
              hasTriggerConditions={hasTriggerConditions}
              isAttributeSegmentTrigger={isAttributeSegmentTrigger}
            />
          )}
          {selectedNode && selectedNode.data?.type === 'trigger' && (
            <TriggerPanel
              key={selectedNode.id}
              onClose={handleCloseSelectedNodePanel}
              onSave={(data) => {
                handleSaveSelectedNode(data, { updateStatus: true });
              }}
              triggerType={
                (selectedNode?.data?.config as any)?.trigger === 'event'
                  ? 'event'
                  : 'attribute-segment'
              }
              event={(selectedNode?.data?.config as any)?.event ?? ''}
              initialFrequency={
                (selectedNode?.data?.config as any)?.frequency ===
                'every_re_match'
                  ? 'every_re_match'
                  : 'one_time'
              }
              conditions={nodeConditions}
              onConditionsChange={(conditions: { [key: string]: any[] }) => {
                setNodeConditions(conditions);
                setNodeConditionsModified(true);
              }}
              conditionsModified={nodeConditionsModified}
              campaignId={id}
            />
          )}
          {selectedNode && selectedNode.data?.type === 'email' && (
            <EmailPanel
              key={selectedNode.id}
              onClose={handleCloseSelectedNodePanel}
              onSave={(data) => {
                handleSaveSelectedNode(data);
              }}
              conditions={nodeConditions}
              onConditionsChange={(conditions: { [key: string]: any[] }) => {
                setNodeConditions(conditions);
                setNodeConditionsModified(true);
              }}
              conditionsModified={nodeConditionsModified}
              campaignId={id}
              selectedNode={selectedNode}
            />
          )}
          {selectedNode && selectedNode.data?.type === 'time-delay' && (
            <DelayPanel
              key={selectedNode.id}
              onClose={handleCloseSelectedNodePanel}
              onSave={(data) => {
                handleSaveSelectedNode(data);
              }}
              conditions={nodeConditions}
              onConditionsChange={(conditions: { [key: string]: any[] }) => {
                setNodeConditions(conditions);
                setNodeConditionsModified(true);
              }}
              conditionsModified={nodeConditionsModified}
              initialDelayValue={
                (selectedNode.data?.config as any)?.delay?.value || 0
              }
              initialDelayUnit={
                (selectedNode.data?.config as any)?.delay?.unit || 'days'
              }
            />
          )}
          {selectedNode && selectedNode.data?.type === 'true-false' && (
            <TrueFalsePanel
              key={selectedNode.id}
              onClose={() => {
                setSelectedNode(null);
                clearReactFlowSelection();
              }}
              onSave={(data) => {
                if (selectedNode) {
                  updateNodeConfig(selectedNode.id, data);
                  clearReactFlowSelection();
                }
              }}
              conditions={(selectedNode.data?.config as any)?.conditions || {}}
            />
          )}
          {selectedNode && selectedNode.data?.type === 'webhook' && (
            <WebhookPanel
              key={selectedNode.id}
              onClose={handleCloseSelectedNodePanel}
              onSave={(data) => {
                handleSaveSelectedNode(data);
              }}
              initialWebhookUrl={(selectedNode.data?.config as any)?.webhookUrl}
              initialMethod={(selectedNode.data?.config as any)?.method}
              initialHeaders={(selectedNode.data?.config as any)?.headers}
              initialPayload={(selectedNode.data?.config as any)?.payload}
            />
          )}
        </Panel>

        <Panel position="bottom-center" className="mb-4">
          <CenterPanel
            selectedNode={selectedNode}
            selectedEdge={selectedEdge}
            deleteSelectedNode={deleteSelectedNode}
          />
        </Panel>
      </ReactFlow>
    </div>
  );
};

const WorkflowStep: React.FC<WorkflowStepProps> = () => {
  return (
    <ReactFlowProvider>
      <WorkflowStepContent />
    </ReactFlowProvider>
  );
};

export default WorkflowStep;
